import axios from 'axios';
import get from 'lodash/get';

import { API, DISTRIBUTOR, DOMAIN } from '@autopass-web/domain';

import storages from './storage';

import type { AuthResponse, UpdateUserQuery } from './types';
import type {
  BulletinBoard,
  CustomBulletinBoard,
  LicensePlate,
  Parkinglot,
  ParkingRecord,
  User,
} from '@autopass-web/types';

const API_CONFIG = DOMAIN.API_URL[API.USER];
const CURRENT_DISTRIBUTOR = DISTRIBUTOR.AUTOPASS;
const DISTRIBUTOR_CLIENT_UID =
  DOMAIN['DISTRIBUTOR_CLIENT_UID'][CURRENT_DISTRIBUTOR];

const axiosInstance = axios.create({
  headers: API_CONFIG.headers,
  baseURL: API_CONFIG.baseUrl,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

axiosInstance.interceptors.request.use(
  (request) => {
    const tokenInfo = storages.getAuth();

    if (tokenInfo) {
      const accessToken = get(storages.getAuth(), 'accessToken', undefined);
      request.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    request.headers['Mochipay-Client-Id'] = DISTRIBUTOR_CLIENT_UID;
    return request;
  },
  (err) => {
    return Promise.reject(err);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err?.response?.status === 401) {
      storages.clearAuth();
    }
    return Promise.reject(err);
  },
);

class Client {
  fetchUserMe = () => axiosInstance.get<{ data: User }>('/me');

  updateUserMe = ({
    enableOnStreetParkingAutoPay,
    defaultGas,
    agreeTermsFfTaipeiCityParkinglotService,
    receipt,
    enablePoint,
  }: UpdateUserQuery) =>
    axiosInstance.post<{ data: User }>('/me', {
      enable_on_street_parking_auto_pay: enableOnStreetParkingAutoPay,
      default_gas: defaultGas,
      agree_terms_of_taipei_city_parkinglot_service:
        agreeTermsFfTaipeiCityParkinglotService,
      receipt,
      enable_point: enablePoint,
    });
  fetchParkinglotBySerialCode = ({
    parkinglotCode,
    serialCode,
  }: {
    parkinglotCode: string;
    serialCode: string;
  }) =>
    axiosInstance.get<{
      data: {
        enabled: boolean;
        name: string;
        serial_number: number;
        scan_source: string;
        parkinglot: Parkinglot;
      };
    }>(`/parking_codes/${serialCode}`, {
      params: { serial_number: parkinglotCode },
    });
  requestOtp = ({ mobile, email }: { mobile: string; email: string }) =>
    axiosInstance.post<void>('/otp', {
      mobile: mobile,
      email: email,
    });

  auth = ({
    mobile,
    email,
    code,
    marketingEmailOptedIn = false,
  }: {
    mobile: string;
    email: string;
    code: string;
    marketingEmailOptedIn: boolean;
  }) =>
    axiosInstance.post<AuthResponse>('/auth', {
      mobile: mobile,
      email: email,
      code: code,
      sign_up_source: 'Web',
      marketing_email_opted_in: marketingEmailOptedIn,
    });

  createLicensePlate = ({
    plateNumber,
    vehicleType,
  }: {
    plateNumber: string;
    vehicleType?: string;
  }) =>
    axiosInstance.post<{ data: LicensePlate }>('/license_plates', {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
    });

  getLicensePlates = () =>
    axiosInstance.get<{ data: LicensePlate[] }>('/license_plates');

  getParkingRecords = () =>
    axiosInstance.get<{ data: ParkingRecord[] }>(`/parking_records`);

  initMochipayValidation = () =>
    axiosInstance.post<{
      data: { transaction_id: string; merchant_uid: string };
    }>('/wallets/init_mochipay_validation');

  queryParkingRecords = (params: {
    parkinglot_id: string;
    plate_number_fuzzy: string;
    vehicle_type: string;
  }) =>
    axiosInstance.get<{
      data: {
        id: string;
        plate_number: string;
        vehicle_type: string;
        checked_in_at: string;
        checked_in_image_url: string;
        is_mochipay_member: boolean;
        store_is_support_alpr: boolean;
        parking_record_kiosk_id: string;
      }[];
    }>(`/parking_records/query_parking`, {
      params,
    });

  createTransactionToken = ({
    token,
    transactionId,
  }: {
    token: string;
    transactionId: string;
  }) =>
    axiosInstance.post('/wallets/mochipay_wallet', {
      token,
      transaction_id: transactionId,
      default_distributor: true,
    });

  fetchBulletinBoards = <
    TData extends Record<
      string,
      Array<BulletinBoard | CustomBulletinBoard<unknown>>
    >,
  >({
    codes,
    partnerId,
    distributor,
  }: {
    codes: string;
    partnerId?: string;
    distributor?: string;
  }) =>
    axiosInstance.get<{
      data: TData;
    }>(`/bulletin_boards`, {
      params: { codes, partner_id: partnerId, distributor },
    });

  sendDownloadAppSms = async () => {
    try {
      return await axiosInstance.post('/sms/download_app');
    } catch (err) {
      // just a promotion api so skip the error.
      console.log(err);
    }
  };
}

export default new Client();
